import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X } from 'lucide-react';
import { loadEstimateSelections } from '../utils/estimateSelections';
import { soundHirePackages, soundAddOns, smokeMachinePackages, venueSupportPackages } from '../data/ubuntuServices';

const WhatsAppButton = ({ currentPage = 'home' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const whatsappBottomClass =
    currentPage === 'estimate' ? 'bottom-24 lg:bottom-6' : 'bottom-6';

  const getPageSpecificMessage = () => {
    const estimateSelections = loadEstimateSelections();
    const soundPackage = soundHirePackages.find((item) => item.id === estimateSelections.soundHirePackageId);
    const smokePackage = smokeMachinePackages.find((item) => item.id === estimateSelections.smokeMachinePackageId);
    const venuePackage = venueSupportPackages.find((item) => item.id === estimateSelections.venueSupportPackageId);
    const selectedAddOns = (estimateSelections.soundAddOnIds || [])
      .map((id) => soundAddOns.find((item) => item.id === id)?.name)
      .filter(Boolean);
    const context = estimateSelections.estimateEventContext || {};
    const structuredEventSummary = `Event details:
- Name: ${context.name || 'Not provided'}
- Date: ${context.eventDate || 'Not provided'}
- Guests: ${context.guests || 'Not provided'}
- Location: ${context.location || 'Not provided'}

Selected services:
- Sound hire: ${soundPackage?.name || 'None'}
- Sound add-ons: ${selectedAddOns.length ? selectedAddOns.join(', ') : 'None'}
- Smoke machine: ${smokePackage?.name || 'None'}
- Venue support: ${venuePackage?.name || 'None'}
- Estimated total: ${estimateSelections.estimatedTotal ? `R${Number(estimateSelections.estimatedTotal).toLocaleString()}` : 'Not calculated'}
`;

    switch (currentPage) {
      case 'menu':
        return "Hi UBUNTU EVENTS MANAGEMENT! I'm interested in your catering services in Gauteng. Could you please send menu options and pricing?";
      case 'spitbraai':
        return "Hi UBUNTU EVENTS MANAGEMENT! I'm interested in spit braai hire in Gauteng. Could you share packages and availability?";
      case 'teambuilding':
        return "Hi UBUNTU EVENTS MANAGEMENT! I'm looking for team building activities for my company. What options do you have available and what are your rates?";
      case 'decor':
        return "Hi UBUNTU EVENTS MANAGEMENT! I'm interested in your event decor services. Could you please show me your available packages and pricing?";
      case 'soundhire':
        return "Hi UBUNTU EVENTS MANAGEMENT! I'm interested in sound hire (speakers + microphones) for my event. Could you recommend the best package for my setup and date?";
      case 'venuesupport':
        return "Hi UBUNTU EVENTS MANAGEMENT! I'm looking for venue booking support. Could you help me find and coordinate a suitable venue for my event?";
      case 'estimate':
        return `Hi Ubuntu Events Management! I'd like to send my estimate request.

${structuredEventSummary}
Please confirm availability and pricing.`;
      case 'contact':
        return "Hi UBUNTU EVENTS MANAGEMENT! I'd like to discuss my event requirements with you. When would be a good time to chat?";
      default:
        return `Hi Ubuntu Events Management! I'm interested in full-service event solutions in Gauteng.

${structuredEventSummary}
Please advise on next steps.`;
    }
  };

  const getPageSpecificQuickMessages = () => {
    switch (currentPage) {
      case 'menu':
        return [
          {
            text: "Get catering quote",
            message: "Hi UBUNTU EVENTS MANAGEMENT! I'd like to get a quote for catering services for my event. Could you please provide pricing information?"
          },
          {
            text: "Check menu availability",
            message: "Hi UBUNTU EVENTS MANAGEMENT! I'd like to check if your catering menu is available for my event date. When are you available?"
          },
          {
            text: "Custom menu request",
            message: "Hi UBUNTU EVENTS MANAGEMENT! I have specific dietary requirements for my event. Can you accommodate custom menu requests?"
          }
        ];
      case 'spitbraai':
        return [
          {
            text: "Spit braai rental quote",
            message: "Hi UBUNTU EVENTS MANAGEMENT! I'd like to get a quote for spit braai rental for my event. What packages do you offer?"
          },
          {
            text: "Check availability",
            message: "Hi UBUNTU EVENTS MANAGEMENT! I'd like to check your spit braai availability for my event date. When are you available?"
          },
          {
            text: "Full service inquiry",
            message: "Hi UBUNTU EVENTS MANAGEMENT! I'm interested in your full service spit braai package. Could you tell me more about what's included?"
          }
        ];
      case 'teambuilding':
        return [
          {
            text: "Team building quote",
            message: "Hi UBUNTU EVENTS MANAGEMENT! I'd like to get a quote for team building activities for my company. What options do you have?"
          },
          {
            text: "Check availability",
            message: "Hi UBUNTU EVENTS MANAGEMENT! I'd like to check your availability for team building activities. When are you available?"
          },
          {
            text: "Custom activities",
            message: "Hi UBUNTU EVENTS MANAGEMENT! I'm looking for custom team building activities. Can you create a program for our specific needs?"
          }
        ];
      case 'decor':
        return [
          {
            text: "Decor package quote",
            message: "Hi UBUNTU EVENTS MANAGEMENT! I'd like to get a quote for event decor services. What packages do you offer?"
          },
          {
            text: "Check availability",
            message: "Hi UBUNTU EVENTS MANAGEMENT! I'd like to check your decor availability for my event date. When are you available?"
          },
          {
            text: "Custom decor request",
            message: "Hi UBUNTU EVENTS MANAGEMENT! I have specific decor requirements for my event. Can you accommodate custom requests?"
          }
        ];
      case 'soundhire':
        return [
          {
            text: "Sound hire quote",
            message: "Hi UBUNTU EVENTS MANAGEMENT! I'd like a quote for sound hire (speakers + microphones). Could you recommend the best package for my event setup?"
          },
          {
            text: "Indoor vs outdoor sound",
            message: "Hi UBUNTU EVENTS MANAGEMENT! I'm hosting an indoor/outdoor event. Which sound package would you recommend and what are the add-ons?"
          },
          {
            text: "Add smoke effects",
            message: "Hi UBUNTU EVENTS MANAGEMENT! I'd like to add smoke effects to my event. Do you have smoke machine packages and what are the prices?"
          }
        ];
      case 'venuesupport':
        return [
          {
            text: "Venue shortlist help",
            message: "Hi UBUNTU EVENTS MANAGEMENT! I'd like help shortlisting venues for my event. Could you advise on your venue search support and pricing?"
          },
          {
            text: "Venue booking assistance",
            message: "Hi UBUNTU EVENTS MANAGEMENT! I'm interested in venue booking assistance. Could you confirm availability, pricing, and next steps?"
          },
          {
            text: "Full venue coordination",
            message: "Hi UBUNTU EVENTS MANAGEMENT! I'd like full venue coordination. How does the process work, and what would you charge for coordination?"
          }
        ];
      case 'estimate':
        return [
          {
            text: "Get event estimate",
            message: "Hi UBUNTU EVENTS MANAGEMENT! I'd like to get a comprehensive estimate for my upcoming event. Could you provide pricing information?"
          },
          {
            text: "Check availability",
            message: "Hi UBUNTU EVENTS MANAGEMENT! I'd like to check your availability for my event date. When are you available?"
          },
          {
            text: "Full service inquiry",
            message: "Hi UBUNTU EVENTS MANAGEMENT! I'm interested in your full event planning service. What packages do you offer?"
          }
        ];
      default:
        return [
          {
            text: "Get a quote for my event",
            message: "Hi UBUNTU EVENTS MANAGEMENT! I'd like to get a quote for my upcoming event. Could you please provide me with pricing information?"
          },
          {
            text: "Check availability",
            message: "Hi UBUNTU EVENTS MANAGEMENT! I'd like to check your availability for my event date. When are you available?"
          },
          {
            text: "General inquiry",
            message: "Hi UBUNTU EVENTS MANAGEMENT! I'm interested in your event services. Could you tell me more about what you offer?"
          }
        ];
    }
  };

  const handleWhatsAppClick = () => {
    const message = getPageSpecificMessage();
    const whatsappUrl = `https://wa.me/27663286231?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const quickMessages = getPageSpecificQuickMessages();

  const handleQuickMessage = (message) => {
    const whatsappUrl = `https://wa.me/27663286231?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
    setIsOpen(false);
  };

  return (
    <>
      {/* Floating WhatsApp Button */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed right-6 z-50 ${whatsappBottomClass} bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-lg transition-all duration-300 transform hover:scale-110`}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 1 }}
      >
        <AnimatePresence mode="wait">
          {isOpen ? (
            <motion.div
              key="close"
              initial={{ rotate: 0 }}
              animate={{ rotate: 0 }}
              exit={{ rotate: 0 }}
            >
              <X className="w-6 h-6" />
            </motion.div>
          ) : (
            <motion.div
              key="whatsapp"
              initial={{ rotate: 0 }}
              animate={{ rotate: 0 }}
              exit={{ rotate: 0 }}
            >
              <MessageCircle className="w-6 h-6" />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.button>

      {/* Quick Message Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.8 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-24 right-6 z-40 bg-white rounded-lg shadow-xl border border-gray-200 p-4 w-80 max-w-sm"
          >
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                Quick Message
              </h3>
              <p className="text-sm text-gray-600">
                Choose a quick message or start a conversation
              </p>
            </div>

            <div className="space-y-2 mb-4">
              {quickMessages.map((item, index) => (
                <button
                  key={index}
                  onClick={() => handleQuickMessage(item.message)}
                  className="w-full text-left p-3 rounded-lg bg-gray-50 hover:bg-gold/10 hover:text-gold transition-colors duration-200 text-sm"
                >
                  {item.text}
                </button>
              ))}
            </div>

            <button
              onClick={handleWhatsAppClick}
              className="w-full bg-green-500 hover:bg-green-600 text-white py-3 px-4 rounded-lg font-semibold transition-colors duration-200 flex items-center justify-center space-x-2"
            >
              <MessageCircle className="w-4 h-4" />
              <span>Start Conversation</span>
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Backdrop */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsOpen(false)}
            className="fixed inset-0 bg-black/20 z-30"
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default WhatsAppButton;